import {
  Box,
  CssBaseline,
  Grid,
  List,
  Slide,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import React, { useState, useEffect } from "react";
import { Route, Switch, useRouteMatch, Redirect } from "react-router";
import { Maintenance } from "./admin/maintenance";
import { Environments } from "./admin/maintenance/environment";
import { Gsap } from "./admin/maintenance/gsap";
import { SystemTypes } from "./admin/maintenance/systemtype";
import { Employees } from "./admin/maintenance/employee";
import { VIPs } from "./admin/maintenance/vip";
import { Categories } from "./admin/maintenance/category";
import { UAM } from "./admin/uam";
import { Workday } from "./admin/workday";
import { FeedbackList } from "./admin/maintenance/feedback";
import { ApproveRequest } from "./approve-request";
import { Details } from "./approve-request/detail";
import { UamDetails } from "./admin/uam/detail";
import { Item } from "./components/menu";
import { Drafts } from "./drafts";
import { Home } from "./home";
import { MyAccess } from "./my-access";
import { MyRequest } from "./my-request";
import { MyTeamAccess } from "./my-team-access";
import { AccessProvisioners } from "./access-provisioners";
import MenuIcon from "@material-ui/icons/Menu";
import { RequestAccessAttestationEmail } from "./request-access-attestation";
import { useTypedSelector } from "@store";
import { Infrastructure } from "./admin/maintenance/infrastructure";
import { SalesforceProfile } from "./admin/maintenance/salesforceprofile";
import { SalesforceRole } from "./admin/maintenance/salesforcerole";
import { SystemAppName } from "./admin/maintenance/systemappname";
import { AccessAdmin } from "./admin/maintenance/accessadmin";
import { InfraApp } from "./admin/maintenance/infraapp";
import { Role } from "./admin/maintenance/role";
import { ApproversDistro } from "./admin/maintenance/approverdistro";
import { AdminDashboard } from "./admin/dashboard";
import { EmployeeDelegates } from "./admin/maintenance/employeedelegates";
import { Announcements } from "./admin/maintenance/announcements/announcements";
import { LandingZones } from "./admin/maintenance/landingzones";
import { LandingZoneRoles } from "./admin/maintenance/landingzoneroles";
import { UnauthorizedPage } from "./UnauthorizedPage";
import { getSession } from "@api";

const useStyles = makeStyles({
  menu: {
    height: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    background: "#F0F0F0",
    paddingLeft: "0px",
    paddingRight: "0px",
  },
  button: {
    "&:hover": {
      cursor: "pointer",
    },
  },
  menuContainer: {
    overflow: "hidden",
  },
});

interface Item {
  label: string;
  icon?: any;
  path?: string;
  subItems?: Item[];
  isAdmin?: boolean;
  isUAM?: boolean;
  isProvisioner?: boolean;
}

const dashboardPath = "/dashboard";

const menuItems: any[] = [
  { label: "Home", icon: "fal fa-home fa-lg", path: "/" },
  { label: "Dashboard", icon: "fal fa-chart-bar fa-lg", path: dashboardPath },
  {
    label: "Create Request",
    icon: "fal fa-file-medical fa-lg",
    path: "/request",
  },
  {
    label: "My Request",
    icon: "fal fa-file-alt fa-lg",
    path: `${dashboardPath}/my-request`,
  },
  {
    label: "Drafts",
    icon: "fal fa-pencil-alt fa-lg",
    path: `${dashboardPath}/drafts`,
  },
  {
    label: "My Access",
    icon: "fal fa-user fa-lg",
    path: `${dashboardPath}/my-access`,
  },
  {
    label: "My Team Access",
    icon: "fal fa-user-friends fa-lg",
    path: `${dashboardPath}/my-team-access`,
  },
  {
    label: "Approve Request",
    icon: "fal fa-file-signature fa-lg",
    path: `${dashboardPath}/approve-request`,
  },
  {
    label: "Access Provisioners",
    icon: "fal fa-file-search fa-lg",
    isProvisioner: true,
    path: `${dashboardPath}/access-provisioners`,
  },
  {
    label: "Access Management",
    icon: "fal fa-shield-alt fa-lg",
    isUAM: true,
    isAdmin: true,
    subItems: [
      {
        isAdmin: true,
        label: "Admin Dashboard",
        path: "/dashboard/admin/dashboard",
      },
      {
        isUAM: true,
        isAdmin: true,
        label: "UAM",
        path: "/dashboard/admin/uam",
      },
      // {
      //   isAdmin: true,
      //   isUAM: true,
      //   label: "For Attestation",
      //   path: "/dashboard/admin/for-attestation",
      // },
      //{ isAdmin: true, label: "Report", path: "/dashboard/admin/report" },
      {
        isAdmin: true,
        label: "Workday File Upload",
        path: "/dashboard/admin/workday-upload",
      },
      {
        isAdmin: true,
        label: "Maintenance",
        path: "/dashboard/admin/maintenance",
      },
      {
        isAdmin: true,
        isUAM: true,
        label: "Feedback List",
        path: "/dashboard/admin/feedback",
      },
      // {
      //   isAdmin: true,
      //   label: "Configuration",
      //   path: "/dashboard/admin/configuration",
      // },
      //{
      //    isAdmin: true,
      //    label: "biz unit",
      //    path: "/dashboard/admin/maintenance/business-unit",
      //},
      //,
      //{
      //    isAdmin: true,
      //    label: "env",
      //    path: "/dashboard/admin/maintenance/environment",
      //},
    ],
  },
];

const displayMenuItem = (
  item: Item,
  isAdmin: boolean,
  isUAM: boolean,
  isProvisioner: boolean
): boolean => {
  let display = true;

  if (item.isUAM && item.isAdmin) {
    display = isUAM ? isUAM : isAdmin;
  } else {
    if (!!item.isUAM) {
      display = isUAM;
    }
    if (!!item.isAdmin) {
      display = isAdmin;
    } else if (!!item.isProvisioner) {
      display = isProvisioner;
    } else {
      display = true;
    }
  }

  return display;
};

export const Dashboard: React.FC = () => {
  const [open, setOpen] = useState<boolean>(true);
  const userGroups: number[] = useTypedSelector(
    (state) => state.session?.userGroups || []
  );
  const [isFetchingSession, setIsFetchingSession] = useState(true);
  const [isAdminFromSession, setIsAdminFromSession] = useState(false);
  const [isUamFromSession, setIsUamFromSession] = useState(false);
  const [isAppOwnerFromSession, setIsAppOwnerFromSession] = useState(false);
  useEffect(() => {
    getSession()
      .then((response) => {
        setIsAdminFromSession(response.userGroups.some((group) => group === 1));
        setIsUamFromSession(response.userGroups.some((group) => group === 4));
        setIsAppOwnerFromSession(
          response.userGroups.some((group) => group === 3)
        );
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setIsFetchingSession(false));
  });

  const isAdmin = userGroups.some((group) => group === 1);
  const isUAM = userGroups.some((group) => group === 4);
  const isProvisioner = userGroups.some((group) => group === 3);
  const { path } = useRouteMatch();
  const classNames = useStyles();
  const dashboardPath = path;
  const myRequestPath = `${dashboardPath}/my-request`;
  const draftsPath = `${dashboardPath}/drafts`;
  const myAccessPath = `${dashboardPath}/my-access`;
  const myTeamAccessPath = `${dashboardPath}/my-team-access`;
  const approveRequestPath = `${dashboardPath}/approve-request`;
  const requestAccessAttestationEmailPath = `${dashboardPath}/requests/:encryptId/accept-attestation`;
  const requestDetailPath = `${dashboardPath}/requests/:encryptId`;
  const requestUAMDetailPath = `${dashboardPath}/admin/uam/requests/:encryptId`;
  const accessProvisionersPath = `${dashboardPath}/access-provisioners`;

  const handleMenuToggle = () => {
    setOpen(!open);
  };

  let checkIfRedirectUamToUnauthorized = null;
  useEffect(() => {
    if (isFetchingSession === false || isUamFromSession === false) {
      checkIfRedirectUamToUnauthorized = (
        <Redirect to="/dashboard/admin/unauthorized" />
      );
    }
  }, [isFetchingSession, isUamFromSession, checkIfRedirectUamToUnauthorized]);

  return (
    <CssBaseline>
      <Grid
        container
        direction="row"
        className={classNames.menuContainer}
        spacing={1}
      >
        <Grid item container xs={open ? 2 : 1}>
          <Box p={1} className={classNames.menu}>
            <Box
              justifyContent="space-between"
              alignItems="baseline"
              onClick={handleMenuToggle}
              className={classNames.button}
              p={1}
            >
              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                <Grid item>
                  <Typography hidden={!open} variant="body1">
                    Menu
                  </Typography>
                </Grid>
                <Grid item>
                  <MenuIcon />
                </Grid>
              </Grid>
            </Box>
            <Slide
              direction="right"
              in={open}
              mountOnEnter
              unmountOnExit
              timeout={1}
            >
              <Box>
                <List component="nav">
                  {menuItems.map((menuItem: Item, index: number) =>
                    displayMenuItem(menuItem, isAdmin, isUAM, isProvisioner) ? (
                      <Item
                        icon={menuItem.icon}
                        label={menuItem.label}
                        path={menuItem.path}
                        subItems={menuItem.subItems}
                        key={index}
                      ></Item>
                    ) : null
                  )}
                </List>
              </Box>
            </Slide>
          </Box>
        </Grid>
        <Grid item xs={open ? 10 : 11}>
          <Switch>
            <Route exact path={dashboardPath}>
              <Home></Home>
            </Route>
            <Route extact path={myRequestPath}>
              <MyRequest></MyRequest>
            </Route>
            <Route exact path={myAccessPath}>
              <MyAccess></MyAccess>
            </Route>
            <Route exact path={myTeamAccessPath}>
              <MyTeamAccess></MyTeamAccess>
            </Route>
            <Route exact path={approveRequestPath}>
              <ApproveRequest></ApproveRequest>
            </Route>
            <Route exact path={draftsPath}>
              <Drafts></Drafts>
            </Route>
            <Route exact path={requestAccessAttestationEmailPath}>
              <RequestAccessAttestationEmail></RequestAccessAttestationEmail>
            </Route>
            <Route exact path={requestDetailPath}>
              <Details></Details>
            </Route>
            <Route exact path={requestUAMDetailPath}>
              {isUamFromSession || isAdminFromSession ? <UamDetails /> : null}
              {isFetchingSession === false &&
              isUamFromSession === false &&
              isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path={accessProvisionersPath}>
              {isAppOwnerFromSession ? <AccessProvisioners /> : null}
              {isFetchingSession === false &&
              isAppOwnerFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance">
              {isAdminFromSession ? <Maintenance /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/uam">
              {isUamFromSession || isAdminFromSession ? <UAM /> : null}
              {isFetchingSession === false &&
              isUamFromSession === false &&
              isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            {/* Removed workday file upload page because the current is file upload from gdrive */}
            {/* <Route exact path="/dashboard/admin/workday-upload">
              {isAdminFromSession ? <Workday /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route> */}
            <Route exact path="/dashboard/admin/maintenance/business-unit">
              {isAdminFromSession ? <SystemTypes /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/environment">
              {isAdminFromSession ? <Environments /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/employees">
              {isAdminFromSession ? <Employees /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/system-category">
              {isAdminFromSession ? <Categories /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route
              exact
              path="/dashboard/admin/maintenance/gsap-security-roles"
            >
              {isAdminFromSession ? <Gsap /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/infrastructure">
              {isAdminFromSession ? <Infrastructure /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route
              exact
              path="/dashboard/admin/maintenance/salesforce-profiles"
            >
              {isAdminFromSession ? <SalesforceProfile /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/salesforce-roles">
              {isAdminFromSession ? <SalesforceRole /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/system-name">
              {isAdminFromSession ? <SystemAppName /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/role">
              {isAdminFromSession ? <Role /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/access-admins">
              {isAdminFromSession ? <AccessAdmin /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/infrastructure-app">
              {isAdminFromSession ? <InfraApp /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/approver-distro">
              {isAdminFromSession ? <ApproversDistro /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/vips">
              {isAdminFromSession ? <VIPs /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/employee-delegates">
              {isAdminFromSession ? <EmployeeDelegates /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/announcements">
              {isAdminFromSession ? <Announcements /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/landing-zones">
              {isAdminFromSession ? <LandingZones /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/maintenance/landing-zone-roles">
              {isAdminFromSession ? <LandingZoneRoles /> : null}
              {isFetchingSession === false && isAdminFromSession === false ? (
                <Redirect to="/dashboard/admin/unauthorized" />
              ) : null}
            </Route>
            <Route exact path="/dashboard/admin/unauthorized">
              <UnauthorizedPage />
            </Route>
            <Route exact path="/dashboard/admin/feedback">
              <FeedbackList />
            </Route>
            <Route exact path="/dashboard/admin/dashboard">
              <AdminDashboard />
            </Route>
          </Switch>
        </Grid>
      </Grid>
    </CssBaseline>
  );
};
